import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useId,
  useRef,
} from 'react';
import {Box, Typography} from '@mui/material';
import type {
  GridColDef,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import ArrDataGridPro from '../../../../common/arr-data-grid-pro/ArrDataGridPro';
import FilterTextField from '../../../../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import CellWrapper from '../../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import {
  ManageIndicesStatus,
  type ManageIndexRow,
} from '../../../../../api/types/aiInvestigate.type';
import ArrTooltip from '../../../../common/arr-tooltip/ArrTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './ManageIndicesDataGrid.scss';

const ROW_HEIGHT = 36;
export const MANAGE_INDICES_DEFAULT_PAGE_SIZE = 25;
export const MANAGE_INDICES_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export type ManageIndicesGridQuery = {
  pageNumber: number;
  pageSize: number;
  sortField?: string;
  sortDirection?: 'ASC' | 'DESC';
  filters: Array<{columnName: string; value: string}>;
};

type Props = {
  rows: ManageIndexRow[];
  rowCount: number;
  setRows: React.Dispatch<React.SetStateAction<ManageIndexRow[]>>;
  onIndexNameClick?: (row: ManageIndexRow) => void;
  onTotalDocsClick?: (row: ManageIndexRow) => void;
  onFilterIndexNameChange?: (value: string) => void;
  onFilterUpdatedByChange?: (value: string) => void;
  onFilterIndexIdChange?: (value: string) => void;
  onActiveToggleChange?: (row: ManageIndexRow, checked: boolean) => void;
  onQueryChange?: (query: ManageIndicesGridQuery) => void;
  loading?: boolean;
};

const ManageIndicesDataGrid: FC<Props> = ({
  rows,
  rowCount,
  setRows,
  onIndexNameClick,
  onTotalDocsClick,
  onFilterIndexNameChange,
  onFilterUpdatedByChange,
  onFilterIndexIdChange,
  onActiveToggleChange,
  onQueryChange,
  loading = false,
}) => {
  const titleId = useId();
  const descId = useId();

  const [filterModel, setFilterModel] = useState<GridFilterModel>({items: []});
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'lastModifiedUtc', sort: 'desc'},
  ]);
  const [pageSize, setPageSize] = useState<number>(
    MANAGE_INDICES_DEFAULT_PAGE_SIZE
  );
  const [pageNumber, setPageNumber] = useState<number>(1);

  const prevFilterModelRef = useRef<GridFilterModel>({items: []});

  const updateRowActive = useCallback(
    (id: number, value: boolean) => {
      setRows((prev) =>
        prev.map((r) => (r.id === id ? {...r, active: value} : r))
      );
    },
    [setRows]
  );

  const handleLinkClick = useCallback(
    (
      type: 'indexName' | 'totalDocs',
      row: ManageIndexRow,
      e: React.MouseEvent<HTMLAnchorElement>
    ) => {
      const isPlainLeftClick =
        e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
      if (!isPlainLeftClick) return;
      e.preventDefault();
      if (type === 'indexName') onIndexNameClick?.(row);
      if (type === 'totalDocs') onTotalDocsClick?.(row);
    },
    [onIndexNameClick, onTotalDocsClick]
  );

  const emitFilterChangeDiffs = useCallback(
    (next: GridFilterModel) => {
      const prev = prevFilterModelRef.current;
      const tracked = new Set(['indexName', 'updatedBy', 'indexId']);
      const getVal = (model: GridFilterModel, field: string) =>
        model.items.find((i) => i.field === field)?.value ?? '';
      tracked.forEach((field) => {
        const prevVal = String(getVal(prev, field));
        const nextVal = String(getVal(next, field));
        if (prevVal !== nextVal) {
          if (field === 'indexName') onFilterIndexNameChange?.(nextVal);
          if (field === 'updatedBy') onFilterUpdatedByChange?.(nextVal);
          if (field === 'indexId') onFilterIndexIdChange?.(nextVal);
        }
      });
      prevFilterModelRef.current = next;
    },
    [onFilterIndexNameChange, onFilterUpdatedByChange, onFilterIndexIdChange]
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);
      setPageNumber(1);
      emitFilterChangeDiffs(model);
    },
    [emitFilterChangeDiffs]
  );

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
    setPageNumber(1);
  }, []);

  const filters = useMemo(() => {
    const tracked = new Set([
      'indexName',
      'updatedBy',
      'indexId',
      'indexStatus',
    ]);
    const columnNameMap: Record<string, string> = {
      indexName: 'IndexName',
      updatedBy: 'UpdatedBy',
      indexId: 'IndexId',
      indexStatus: 'IndexStatus',
    };
    return filterModel.items
      .filter((item) => item?.field && tracked.has(item.field))
      .map((item) => ({
        columnName: columnNameMap[String(item.field)] ?? String(item.field),
        value: String(item.value ?? '').trim(),
      }))
      .filter((item) => item.value !== '');
  }, [filterModel]);

  useEffect(() => {
    const sortItem = sortModel[0];
    const effectiveSortField = sortItem?.field ?? 'lastModifiedUtc';
    let effectiveSortDirection: 'ASC' | 'DESC' = 'DESC';
    if (sortItem?.sort === 'asc') {
      effectiveSortDirection = 'ASC';
    }
    onQueryChange?.({
      pageNumber,
      pageSize,
      sortField: effectiveSortField,
      sortDirection: effectiveSortDirection,
      filters,
    });
  }, [filters, onQueryChange, pageNumber, pageSize, sortModel]);

  const wrap = useCallback((content: ReactNode, cellParams: any) => {
    const cell = (
      <CellWrapper
        toolTipContent={cellParams.value}
        dataTestId={`tooltip-manage-datagrid-cellwrapper-${cellParams.id}`}
        cellParams={cellParams}
      >
        {content}
      </CellWrapper>
    );
    return cell;
  }, []);

  const textCell = useCallback((value: any, dataTestId: string) => {
    return (
      <Typography
        component="span"
        className="mi-cell__text"
        data-testid={`manage-indices-data-grid-text${dataTestId}`}
      >
        {value ?? ''}
      </Typography>
    );
  }, []);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'null',
        headerName: '',
        sortable: false,
        filterable: false,
        minWidth: 32,
        width: 32,
        resizable: false,
        disableReorder: true,
        cellClassName: 'mi-cell',
      },
      {
        field: 'indexId',
        headerName: t('ai.investigate.manage-indices.indexId'),
        sortable: true,
        filterable: false,
        minWidth: 148.5,
        cellClassName: 'mi-cell',
        renderCell: (params) =>
          wrap(textCell(params.value, `indexId-${params.id}`), params),
        renderHeaderFilter: (params) => (
          <FilterTextField
            dataTestId="indexId"
            gridRenderHeaderFilterProps={params}
            placeholder={t('common.filter', 'Filter')}
          />
        ),
      },
      {
        field: 'indexName',
        headerName: t('ai.investigate.manage-indices.indexName'),
        sortable: true,
        filterable: true,
        minWidth: 253.5,
        flex: 1,
        cellClassName: 'mi-cell',
        renderCell: (params) => {
          const value = String(params.value ?? '');
          const href = `#index/${encodeURIComponent(
            String(params.row?.indexId ?? params.value ?? '')
          )}`;
          return wrap(
            <a
              className="mi-cell__link"
              href={href}
              tabIndex={params.tabIndex}
              data-testid={`{index-name-${params.id}`}
              onClick={(e) =>
                handleLinkClick('indexName', params.row as ManageIndexRow, e)
              }
            >
              <span
                className="mi-cell__link-text"
                data-testid="manage-indices-data-grid-span"
              >
                {value}
              </span>
            </a>,
            params
          );
        },
        renderHeaderFilter: (params) => (
          <FilterTextField
            dataTestId="indexName"
            gridRenderHeaderFilterProps={params}
            placeholder={t('common.filter', 'Filter')}
          />
        ),
      },
      {
        field: 'updatedBy',
        headerName: t('ai.investigate.manage-indices.updatedBy'),
        sortable: true,
        filterable: true,
        minWidth: 253.5,
        flex: 1,

        cellClassName: 'mi-cell',
        renderCell: (params) =>
          wrap(textCell(params.value, `updatedBy-${params.id}`), params),
        renderHeaderFilter: (params) => (
          <FilterTextField
            dataTestId="updatedBy"
            gridRenderHeaderFilterProps={params}
            placeholder={t('common.filter', 'Filter')}
          />
        ),
      },

      {
        field: 'active',
        headerName: t('ai.investigate.manage-indices.active'),
        sortable: true,
        filterable: false,
        minWidth: 130,
        align: 'center',
        headerAlign: 'center',
        cellClassName: 'mi-cell',
        renderHeader: () => (
          <Box className="mi-header mi-header--with-help" component="div">
            <Typography
              component="span"
              className="mi-header__label"
              data-testid="manage-indices-data-grid-active"
            >
              {t('ai.investigate.manageIndices.grid.active', 'Active')}
            </Typography>
            <ArrTooltip
              trigger="click"
              placement="top"
              offset={4}
              className="arr-tooltip--opaque"
              contentClassName="arr-tooltip__content"
              content={t('ai.investigate.manage-indices.activeTooltip')}
              labelId={`header-active`}
            >
              <Box
                className="mi-header__info"
                aria-label={t(
                  'ai.investigate.manageIndices.grid.active.helpAria',
                  'Help for Active column'
                )}
                role="img"
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </Box>
            </ArrTooltip>
          </Box>
        ),
        renderCell: (params) => {
          const indexName =
            (typeof params.row?.indexName === 'string'
              ? params.row.indexName.trim()
              : '') || String(params.row?.id ?? '');
          const toggleLabel = t(
            'ai.investigate.manageIndices.grid.activeToggleAria',
            'Toggle active status for {{indexName}}',
            {indexName}
          );

          return wrap(
            <ArrToggle
              toggleSize="large"
              ariaLabel={toggleLabel}
              dataTestId={`active-toggle-${params.row.id}`}
              value={params.row.active}
              className="mi-active-toggle"
              tabIndex={params.tabIndex}
              onChange={(_, checked) => {
                onActiveToggleChange?.(params.row as ManageIndexRow, checked);
                updateRowActive(params.row.id, checked);
              }}
            />,
            params
          );
        },
      },
      {
        field: 'totalDocs',
        headerName: t('ai.investigate.manage-indices.totalDocs'),
        sortable: true,
        filterable: false,
        minWidth: 160,
        cellClassName: 'mi-cell',
        renderCell: (params) => {
          const n = Number(params.value);
          const isTotalDocsClickable = Number.isFinite(n) && n > 0;
          const formatted = Number.isFinite(n) ? n.toLocaleString() : '—';

          if (!isTotalDocsClickable) {
            return wrap(
              <span
                className="mi-cell__link mi-cell__text--disabled"
                data-testid={`total-docs-disabled-${params.id}`}
              >
                {formatted}
              </span>,
              params
            );
          }
          const href = `#index/${encodeURIComponent(
            String(params.row?.indexId ?? '')
          )}/docs`;
          return wrap(
            <a
              className="mi-cell__link mi-cell__link--numeric"
              href={href}
              tabIndex={params.tabIndex}
              data-testid={`{total-docs-${params.id}`}
              onClick={(e) =>
                handleLinkClick('totalDocs', params.row as ManageIndexRow, e)
              }
            >
              <span
                className="mi-cell__link-text mi-cell__link-text--numeric"
                data-testid="manage-indices-data-grid-span-btn"
              >
                {formatted}
              </span>
            </a>,
            params
          );
        },
      },
      {
        field: 'lastModifiedUtc',
        headerName: t('ai.investigate.manage-indices.lastModifiedDate'),
        sortable: true,
        filterable: false,
        minWidth: 216.5,
        flex: 1,
        cellClassName: 'mi-cell',
        renderCell: (params) =>
          wrap(textCell(params.value, `lastModified-${params.id}`), params),
      },
      {
        field: 'indexStatus',
        headerName: t('ai.investigate.manage-indices.status'),
        sortable: true,
        filterable: true,
        minWidth: 216.5,
        cellClassName: 'mi-cell',
        flex: 1,
        renderHeaderFilter: (params) => (
          <FilterTextField
            dataTestId="status"
            gridRenderHeaderFilterProps={params}
            placeholder={t('common.filter', 'Filter')}
          />
        ),
        renderCell: ({value, id}) => {
          const cellValue = String(value);

          const getStatusClass = () => {
            switch (cellValue) {
              case ManageIndicesStatus.Completed:
                return 'manage-indices__status--completed';
              case ManageIndicesStatus.InProgress:
                return 'manage-indices__status--progress';
              case ManageIndicesStatus.Failed:
                return 'manage-indices__status--failed';
              case ManageIndicesStatus.InQueue:
                return 'manage-indices__status--queue';
              case ManageIndicesStatus.InRetryQueue:
                return 'manage-indices__status--retry';
              default:
                return '';
            }
          };

          return (
            <Box className="status-box">
              <Typography
                className={`manage-indices__status ${getStatusClass()} cell-content typo-text body3_size`}
                data-testid={`status-${id}`}
              >
                {t(
                  `ai.investigate.manageIndicesDetail.manageIndicesDataGrid.statusValue.${cellValue}`,
                  cellValue
                )}
              </Typography>
            </Box>
          );
        },
        headerClassName: 'mi-header mi-header--no-divider',
      },
    ];
  }, [textCell, updateRowActive, wrap, handleLinkClick, onActiveToggleChange]);

  return (
    <Box
      className="manage-indices-datagrid"
      component="section"
      role="region"
      aria-labelledby={titleId}
      aria-describedby={descId}
      aria-busy={loading}
    >
      <Typography
        id={titleId}
        component="h2"
        className="sr-only"
        data-testid="manage-indices-data-grid-title"
      >
        {t('ai.investigate.manageIndices.grid.title', 'Indices table')}
      </Typography>
      <Typography
        id={descId}
        component="p"
        className="sr-only"
        data-testid="manage-indices-data-grid-header-tooltip"
      >
        {t('ai.investigate.manage-indices.headerTooltip')}
      </Typography>
      <ArrDataGridPro
        data-testid="manage-indices-data-grid"
        rowHeight={ROW_HEIGHT}
        primaryKey="id"
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        pagination
        currentPage={pageNumber}
        itemsPerPage={pageSize}
        setItemsPerPage={setPageSize}
        pageSizeOptions={[...MANAGE_INDICES_PAGE_SIZE_OPTIONS]}
        disableColumnResize={false}
        noRowsOverlayCustomText={t(
          'ai.investigate.manage-indices.messageNoData'
        )}
        showPerPageOptions={true}
        onPageChange={(p, ps) => {
          setPageNumber(p);
          if (ps) setPageSize(ps);
        }}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        headerFilters
        disableColumnFilter
        enableCheckbox={false}
        disableVirtualization={false}
        entityType={t('ai.investigate.manageIndices.grid.entity', 'indices')}
        filterModel={filterModel}
        sortModel={sortModel}
        onFilterModelChange={handleFilterModelChange}
        onSortModelChange={handleSortModelChange}
        initialState={{
          pagination: {
            paginationModel: {
              page: pageNumber - 1,
              pageSize: pageSize,
            },
          },
        }}
      />
    </Box>
  );
};

export default ManageIndicesDataGrid;
